/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState, useEffect, useRef } from 'react';
import { Box } from '@/shared/ui';
import { useMediaQuery } from '@/shared/hooks/use-media-query';
import { useTranslation } from 'react-i18next';
import { Link, matchPath, useLocation, useNavigate } from 'react-router';
import { useAuth } from '@context/authentication';
import { changeLanguage, getCurrentLanguage, getAvailableLanguages } from '@/shared/i18n';
import { cn } from '@/shared/ui/utils/tailwind';
import { UserAvatarButton } from './UserAvatarButton';
import { HousienBrandLogo } from '@/shared/ui/components/basic/logos';
import { useAuthenticatedImage } from '@/shared/hooks/use-authenticated-image';
import { normalizeLanguage, scrollToLandingSection, scrollLandingContentToTop } from './GlobalHeader.helpers';
import { useActiveSection } from './use-active-section';
import { CenterNav } from './GlobalHeader.center-nav';
import { GuestActions } from './GlobalHeader.actions';
import { FeedbackTrigger } from './FeedbackTrigger';
import HeaderFeedbackPopup from './HeaderFeedbackPopup';

// Marketing-nav links (Recursos/Preços) only make sense on the site's real
// public pages. A 404 or /error page matches none of these, so the nav stays
// hidden there instead of implying more marketing content that doesn't exist.
const MARKETING_NAV_PATTERNS = [
  '/', '/early-access', '/early-access/', '/design-system/*', '/pricing',
  '/prototype/*', '/prototypes/*', '/blog/*', '/serviceterms/*', '/privacy/*',
  '/cookies/*', '/data-storage/*', '/acceptable-use/*', '/checkout/success',
  '/p/:agency_slug/:property_slug',
];

type GlobalHeaderProps = {
  /**
   * Drops the rule under the bar, for a screen that opens straight into its
   * own content: the workspace does it by being on the borderless list, and a
   * screen rendering the bar itself asks for the same here.
   */
  borderless?: boolean;
};

const GlobalHeader: React.FC<GlobalHeaderProps> = ({ borderless = false }) => {
  const { t, i18n } = useTranslation('@pages/landing');
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const navRef = useRef<HTMLDivElement | null>(null);
  const [hoveredNav, setHoveredNav] = useState<string | null>(null);
  const profilePictureUrl = useAuthenticatedImage(
    auth.user?.profile?.picture as string | undefined,
  );

  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState<string>(normalizeLanguage(getCurrentLanguage()));
  const isMobile = useMediaQuery('(max-width: 868px)');

  useEffect(() => {
    const handleLanguageChange = (lng: string): void => setCurrentLanguage(normalizeLanguage(lng));
    i18n.on('languageChanged', handleLanguageChange);
    setCurrentLanguage(normalizeLanguage(i18n.language));
    return () => { i18n.off('languageChanged', handleLanguageChange); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n]);

  const handleLanguageChange = (lang: string): void => {
    changeLanguage(lang);
    setCurrentLanguage(normalizeLanguage(lang));
  };

  const goToHome = (e?: React.MouseEvent): void => {
    if (location.pathname !== '/') {
      navigate('/');
      return;
    }
    if (e) e.preventDefault();
    if (location.hash) navigate('/', { replace: true });
    scrollLandingContentToTop();
  };

  const isOnboardingRoute =
    location.pathname === '/app/workspace' ||
    location.pathname === '/app/workspace/' ||
    location.pathname === '/app/agencies' ||
    location.pathname === '/app/agencies/';
  const isLandingPage = location.pathname === '/';
  const isBlogPage = location.pathname.startsWith('/blog');
  const isEarlyAccessPage = location.pathname.startsWith('/early-access');
  const isAppPage = location.pathname.startsWith('/app');
  const isMarketingPage = MARKETING_NAV_PATTERNS.some((pattern) => matchPath(pattern, location.pathname));

  // NOTE: an authenticated user must still be able to read the public landing
  // page (pricing, features, blog entry points). A previous effect here bounced
  // every signed-in visit to `/` straight to `/app/workspace`, making the
  // marketing site unreachable while logged in. Entering the app is a deliberate
  // action — the header's user menu and the hero's dashboard CTA both offer it.

  const activeSection = useActiveSection(isLandingPage, location.pathname);
  const sections = {
    levels: isLandingPage && activeSection === 'levels',
    recursos: isLandingPage && activeSection === 'recursos',
    faq: isLandingPage && activeSection === 'faq',
    features: isLandingPage && activeSection === 'features',
    pricing: isLandingPage && activeSection === 'pricing',
  };

  return (
    <Box
      ref={navRef}
      component="header"
      className={cn(
        'fixed top-0 left-0 right-0 z-1000 h-(--header-height) w-full',
        'flex items-center px-(--space-5) sm:px-(--space-6) md:px-(--space-8)',
        !isOnboardingRoute && !borderless && 'border-b border-(--border-subtle)',
        isAppPage ? 'bg-(--surface-primary)' : 'bg-transparent backdrop-blur-[10px]',
        'transition-[backdrop-filter] duration-150 ease-in-out',
      )}
    >
      <Link
        to={isAppPage ? '/app/workspace' : '/'}
        aria-label="Housien"
        className={cn(
          'flex shrink-0 gap-0',
          isMobile ? 'items-center gap-(--space-2)' : 'items-end',
          'no-underline! cursor-pointer transition-opacity duration-200 hover:opacity-80',
          'hover:no-underline! focus:no-underline! active:no-underline! visited:no-underline! **:no-underline!',
        )}
        onClick={isAppPage ? undefined : goToHome}
      >
        {isMobile ? (
          <HousienBrandLogo variant="full" height={60} className="-translate-y-0.5" />
        ) : (
          <HousienBrandLogo variant="full" />
        )}
      </Link>

      {!isMobile && !isAppPage && isMarketingPage && (
        <CenterNav
          sections={sections}
          isAuthenticated={auth.isAuthenticated}
          isEarlyAccessPage={isEarlyAccessPage}
          isBlogPage={isBlogPage}
          hoveredNav={hoveredNav}
          setHoveredNav={setHoveredNav}
          onScroll={(id) => scrollToLandingSection(id, location.pathname, navigate)}
        />
      )}

      <div className="flex items-center gap-(--space-3) shrink-0 ml-auto">
        {auth.isLoading ? null : auth.isAuthenticated ? (
          <>
            <FeedbackTrigger onClick={() => setIsFeedbackOpen(true)} label={t('navigation.feedback')} />
            <UserAvatarButton
              fullName={auth.user?.profile?.full_name as string || auth.user?.profile?.name as string || ''}
              photoUrl={profilePictureUrl}
              onClick={() => navigate('/app/profile')}
              ariaLabel={t('navigation.userMenu')}
            />
          </>
        ) : (
          <GuestActions
            isMobile={isMobile}
            onSignIn={() => auth.signin()}
            currentLanguage={currentLanguage}
            availableLanguages={getAvailableLanguages()}
            onLanguageChange={handleLanguageChange}
          />
        )}
      </div>

      <HeaderFeedbackPopup visible={isFeedbackOpen} setVisible={setIsFeedbackOpen} />

    </Box>
  );
};

export default GlobalHeader;
